import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiDraft,
  createActivityLogEntry,
  deleteActivityLogEntry,
  getProject,
  listNotes,
  listPhaseTasks,
  patchAICall,
  patchActivityLogEntry,
  VALID_LOG_TYPES,
  type ActivityLogEntry,
  type AIProviderName,
  type LogType,
} from '../lib/api';
import { FlagIcon, UrgentBadge } from './UrgentBadge';
import { ConfirmationDialog } from './ConfirmationDialog';

/**
 * ClientUpdateComposeModal — compose / edit a client-facing update entry
 * (ARCH §3.5, Build C.1; refreshed D-106).
 *
 * An activity_log entry has BOTH a `private_entry` (internal note, never
 * crosses to the client portal) and a `published_summary` (what the
 * client sees, only when `is_published=1`). The compose modal lets C
 * draft both, optionally with the AI drafter populating the published
 * summary based on a prompt + the section's private notes context.
 *
 *   - Mode "create" — opens with sensible defaults (today's date, log_type
 *     'Note', section pre-filled from the current Notes section).
 *   - Mode "edit"   — pre-fills from an existing entry. Save updates;
 *     "Delete" removes the entry.
 *
 * On save, the entry can land as a draft (`is_published=0`, internal-only)
 * or as a published update (`is_published=1`, visible on the client portal
 * once Build E ships). The toggle is right next to the save buttons so the
 * publish action is deliberate — D-006: "Strict draft-and-publish for
 * client content."
 *
 * D-106 refresh:
 *   - Gray docPagePalette inside the dialog (matches the locked tracker
 *     visual standard from D-088 → D-093).
 *   - Phase selector (`phase_id`) — column already existed; was unexposed.
 *   - Section selector — was a free-text input where the user had to type
 *     the exact key; now a dropdown populated from the project's notes
 *     sections via listNotes().
 *   - Urgent toggle (`is_urgent`) — new column from migration 014. Visual
 *     flag mirroring tasks.is_critical; doesn't affect sort order.
 *   - Linked task selector (`task_id`) — column already existed; activates
 *     once a phase is picked, lists that phase's tasks so an update can
 *     be tied to a specific completion event.
 *
 * AI drafter integration (D-036):
 *   - Provider dropdown: Mock (default — deterministic, no network),
 *     Local Qwen (`localhost:8080`), Claude (cloud).
 *   - "Draft with AI" button posts to `/api/ai/draft` with the user prompt
 *     + section_key for context. Response text fills `published_summary`.
 *   - On save, if the published_summary differs from the AI's original
 *     output, we patch `ai_calls.response_edited=1` so usage stats can
 *     answer "of N drafts, how many landed unedited?"
 */

export type ComposeModalMode =
  | { kind: 'create' }
  | { kind: 'edit'; entry: ActivityLogEntry };

export function ClientUpdateComposeModal({
  open,
  onOpenChange,
  projectId,
  defaultPhaseId,
  defaultTaskId,
  mode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** D-107 — pre-selects the Phase dropdown when opening in create mode.
      Lets callers that open the modal from within a phase context land
      the new entry tagged to that phase by default. Ignored in edit mode
      (the existing entry's phase_id wins). */
  defaultPhaseId?: string | null;
  /** D-111 — pre-selects the Linked task dropdown when opening in create
      mode. Used by the "+ note" affordance on a task row so the quick-jot
      path produces a Client Update tagged to that task automatically.
      Requires defaultPhaseId to be set (linked task selector activates
      only when a phase is picked). Ignored in edit mode. */
  defaultTaskId?: string | null;
  mode: ComposeModalMode;
}) {
  const qc = useQueryClient();

  // Form state — initialized once per modal open so closing+reopening
  // resets the form. The mode object's identity changes when the parent
  // switches between create / edit-X / edit-Y, which is what we key off.
  const [entryTitle, setEntryTitle] = useState<string>('');
  const [logType, setLogType] = useState<LogType>('Note');
  const [eventTimestamp, setEventTimestamp] = useState<string>(todayIso());
  const [phaseId, setPhaseId] = useState<string>('');
  const [taskId, setTaskId] = useState<string>('');
  const [privateEntry, setPrivateEntry] = useState<string>('');
  const [publishedSummary, setPublishedSummary] = useState<string>('');
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const [isUrgent, setIsUrgent] = useState<boolean>(false);

  // AI drafter row state.
  // D-109 — section_key dropped from the modal UI. The AI drafter still
  // needs to know which notes bucket to read, but instead of asking the
  // user to pick a section explicitly (a confusing internal pointer), we
  // expose a single dropdown listing every phase + a "Whole project"
  // option at the top. Default is whole-project so the AI gets the
  // broadest context by default; user opts in to a phase narrow when
  // they want focused source material.
  // D-110: replaced the two-mode `aiContextScope` toggle with a single
  // dropdown keyed on phase_id. Empty string = whole project.
  const [aiProvider, setAiProvider] = useState<AIProviderName>('mock');
  const [aiContextPhaseId, setAiContextPhaseId] = useState<string>('');
  const [aiPrompt, setAiPrompt] = useState<string>('');
  const [aiCallId, setAiCallId] = useState<string | null>(null);
  const [aiOriginalText, setAiOriginalText] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  // D-110 — delete uses the same red ConfirmationDialog the rest of the app
  // uses (D-050), not the browser's native confirm() popup. State opens
  // the dialog; the dialog itself fires the mutation on confirm.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Project context for the Phase + Section dropdowns. Both are project-
  // scoped and small (≤15 phases, ≤15 sections). We refetch lazily — only
  // when the modal opens — and let TanStack Query cache hand back the
  // payload on subsequent opens without a network hit.
  const projectQ = useQuery({
    queryKey: ['projects', 'one', projectId],
    queryFn: () => getProject(projectId),
    enabled: open,
  });
  const notesQ = useQuery({
    queryKey: ['notes', 'list', projectId],
    queryFn: () => listNotes(projectId),
    enabled: open,
  });

  // Linked task selector — only enabled once a phase is picked. Lazy-load
  // tasks for the chosen phase. The phase's task list typically fits in
  // 5-15 rows so the dropdown stays scannable.
  const phaseTasksQ = useQuery({
    queryKey: ['tasks', 'phase', phaseId],
    queryFn: () => listPhaseTasks(phaseId),
    enabled: open && !!phaseId,
  });

  // (Re)initialize the form whenever the modal opens (or `mode` changes
  // because the user clicked a different entry to edit).
  useEffect(() => {
    if (!open) return;
    if (mode.kind === 'create') {
      setEntryTitle('');
      setLogType('Note');
      setEventTimestamp(todayIso());
      setPhaseId(defaultPhaseId ?? '');
      setTaskId(defaultTaskId ?? '');
      setPrivateEntry('');
      setPublishedSummary('');
      setIsPublished(false);
      setIsUrgent(false);
    } else {
      const e = mode.entry;
      setEntryTitle(e.entry_title ?? '');
      setLogType(e.log_type);
      setEventTimestamp(e.event_timestamp.slice(0, 10));
      setPhaseId(e.phase_id ?? '');
      setTaskId(e.task_id ?? '');
      setPrivateEntry(e.private_entry ?? '');
      setPublishedSummary(e.published_summary ?? '');
      setIsPublished(e.is_published === 1);
      setIsUrgent(e.is_urgent === 1);
    }
    setAiProvider('mock');
    setAiContextPhaseId('');
    setAiPrompt('');
    setAiCallId(null);
    setAiOriginalText(null);
    setAiError(null);
    setConfirmDeleteOpen(false);
  }, [open, mode, defaultPhaseId, defaultTaskId]);

  // D-110-fix — Previous version used a useEffect on phaseId-change to
  // clear taskId. That had a subtle bug on edit-mode open: the effect's
  // closure captured the initial empty phaseId, decided "phase changed",
  // and cleared taskId BEFORE the init effect's phaseId update propagated.
  // Result: opening an entry with a linked task showed "none" in the
  // dropdown. Fix: clear taskId in the Phase dropdown's onChange handler
  // (real user action only, not initial-render flicker). See `onPhaseChange`
  // below — used as the Phase select's onChange.
  const onPhaseChange = (next: string) => {
    setPhaseId(next);
    // Stale task_id from a different phase wouldn't appear in the new
    // phase's task list. Clear so the user makes a fresh choice.
    setTaskId('');
  };

  const draftMut = useMutation({
    mutationFn: async () => {
      const prompt = aiPrompt.trim();
      if (!prompt) throw new Error('Type a prompt for the AI drafter.');
      setAiError(null);
      // D-110 — the AI source dropdown picks ANY phase or "whole project"
      // (empty string). Whole project = no section_key (server pulls
      // project-wide context). Phase = derive section_key from that phase
      // and pass it to the AI drafter endpoint.
      const sectionKeyForAi = aiContextPhaseId
        ? deriveSectionKeyFromPhase(aiContextPhaseId, sortedPhases, sortedSections)
        : undefined;
      const res = await aiDraft({
        project_id: projectId,
        user_prompt: prompt,
        section_key: sectionKeyForAi,
        mode: 'drafter',
        provider: aiProvider,
      });
      setPublishedSummary(res.text);
      setAiCallId(res.ai_call_id);
      setAiOriginalText(res.text);
      return res;
    },
    onError: (e: Error) => {
      setAiError(e.message);
    },
  });

  const saveMut = useMutation({
    mutationFn: async (publish: boolean) => {
      const eventTs = new Date(eventTimestamp + 'T12:00:00Z').toISOString();
      const body = {
        entry_title: entryTitle.trim() || null,
        log_type: logType,
        event_timestamp: eventTs,
        // D-109 — section_key is no longer a user-facing field. Saved as
        // null for new entries; in edit mode we don't touch it so any
        // legacy section_key from before D-109 is preserved on the row.
        section_key: mode.kind === 'edit' ? (mode.entry.section_key ?? null) : null,
        phase_id: phaseId || null,
        task_id: taskId || null,
        private_entry: privateEntry,
        published_summary: publishedSummary,
        is_published: publish,
        is_urgent: isUrgent ? 1 : 0,
      } as const;
      if (mode.kind === 'create') {
        await createActivityLogEntry(projectId, body);
      } else {
        await patchActivityLogEntry(mode.entry.id, body);
      }
      // If an AI draft fed the published_summary and the user changed it
      // before saving, mark the AI call as edited (so usage stats can
      // answer "of N drafts, how many landed unedited?").
      if (aiCallId && aiOriginalText != null) {
        const edited = aiOriginalText !== publishedSummary;
        try {
          await patchAICall(aiCallId, {
            response_committed: 1,
            response_edited: edited ? 1 : 0,
          });
        } catch {
          // Non-fatal — usage stats are not load-bearing.
        }
      }
      await qc.invalidateQueries({
        queryKey: ['activity-log', projectId],
      });
    },
    onSuccess: () => onOpenChange(false),
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      if (mode.kind !== 'edit') return;
      await deleteActivityLogEntry(mode.entry.id);
      await qc.invalidateQueries({
        queryKey: ['activity-log', projectId],
      });
    },
    onSuccess: () => onOpenChange(false),
  });

  // D-106 — page-scoped gray palette override applied inside the modal so
  // its surfaces match the locked tracker standard. Modal content is
  // portaled to <body>, so it does NOT inherit any docPagePalette from a
  // page wrapper — we apply it here directly.
  const docPagePalette: React.CSSProperties = {
    ['--color-page-bg' as string]: '#F3F4F6',
    ['--color-card' as string]: '#FFFFFF',
    ['--color-section-header' as string]: '#D1D5DB',
    ['--color-border' as string]: '#D1D5DB',
    ['--color-border-strong' as string]: '#9CA3AF',
    ['--color-surface' as string]: '#F3F4F6',
    ['--color-surface-2' as string]: '#E5E7EB',
  };

  const phases = projectQ.data?.phases ?? [];
  const sortedPhases = [...phases].sort((a, b) => a.phase_number - b.phase_number);
  const sections = notesQ.data?.notes ?? [];
  const sortedSections = [...sections].sort((a, b) => a.sort_order - b.sort_order);
  const phaseTasks = phaseTasksQ.data?.tasks ?? [];
  const sortedTasks = [...phaseTasks].sort((a, b) => a.task_number - b.task_number);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: 'rgba(28, 38, 56, 0.55)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(760px,95vw)] max-h-[92vh] overflow-auto border rounded-lg shadow-2xl"
          style={{
            ...docPagePalette,
            background: 'var(--color-card)',
            borderColor: 'var(--color-border)',
          }}
          aria-describedby={undefined}
        >
          <header
            className="px-5 py-3 border-b flex items-center justify-between rounded-t-lg"
            style={{
              background: 'var(--color-section-header)',
              borderColor: 'var(--color-border)',
            }}
          >
            <div>
              <Dialog.Title className="text-lg font-semibold text-ink">
                {mode.kind === 'create' ? 'New client update' : 'Edit client update'}
              </Dialog.Title>
              <p className="text-xs mt-0.5 flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="inline-block w-2 h-2 rounded-full"
                  style={{
                    background: isPublished
                      ? 'var(--color-success)'
                      : 'var(--color-danger)',
                  }}
                />
                <span
                  className="font-medium"
                  style={{
                    color: isPublished
                      ? 'var(--color-success)'
                      : 'var(--color-danger)',
                  }}
                >
                  {isPublished ? 'Published' : 'Draft'}
                </span>
                <span className="text-ink-2">
                  {' '}·{' '}
                  {isPublished
                    ? 'visible on the client portal'
                    : 'internal only until you publish'}
                </span>
                {isUrgent && (
                  <>
                    <span className="text-ink-3 ml-1">·</span>
                    <UrgentBadge />
                  </>
                )}
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="text-ink-2 hover:text-ink text-sm px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label="Close"
              >
                ✕
              </button>
            </Dialog.Close>
          </header>

          <div className="p-5 space-y-4">
            {/* D-109 — Headline (was "Title") is the row's identity.
                Stronger label + clearer help text per the D-109 label
                pass. */}
            <Field
              label="Headline"
              hint="What this update is, in a few words. Shows as the prominent title on every row."
            >
              <input
                type="text"
                value={entryTitle}
                onChange={(e) => setEntryTitle(e.target.value)}
                placeholder="e.g. Permit Round 1 Comments Returned"
                className={`${inputClass} text-base font-medium`}
                autoFocus
              />
            </Field>

            {/* Row 1: Category / Event date / Urgent toggle */}
            <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <Field
                label="Category"
                hint="What kind of activity this is. Shows on the row subline."
              >
                <select
                  value={logType}
                  onChange={(e) => setLogType(e.target.value as LogType)}
                  className={selectClass}
                >
                  {VALID_LOG_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Event date"
                hint="When this happened — not when you wrote it. Drives feed order."
              >
                <input
                  type="date"
                  value={eventTimestamp}
                  onChange={(e) => setEventTimestamp(e.target.value)}
                  className={`${inputClass} num`}
                />
              </Field>
              <UrgentToggle value={isUrgent} onChange={setIsUrgent} />
            </div>

            {/* Row 2: File under phase / Link to a task (Section dropped in D-109) */}
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="File under phase"
                hint="Tags this update to a phase. Shows on the row as a chip; updates linked to a task appear inline under that task."
              >
                <select
                  value={phaseId}
                  onChange={(e) => onPhaseChange(e.target.value)}
                  className={selectClass}
                  disabled={projectQ.isLoading}
                >
                  <option value="">— none —</option>
                  {sortedPhases.map((p) => (
                    <option key={p.id} value={p.id}>
                      {String(p.phase_number).padStart(2, '0')} — {p.phase_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Link to a task"
                hint={
                  !phaseId
                    ? 'Pick a phase above first to see its tasks.'
                    : 'Optional. The linked task shows this update inline; the update references the task.'
                }
              >
                <select
                  value={taskId}
                  onChange={(e) => setTaskId(e.target.value)}
                  className={selectClass}
                  disabled={!phaseId || phaseTasksQ.isLoading}
                >
                  <option value="">— none —</option>
                  {/* D-110-fix — defensive placeholder so the current
                      task_id always has a matching option in the select,
                      even before phaseTasksQ resolves. Without this, the
                      browser falls back to "— none —" visually on edit-mode
                      open until the tasks finish loading. */}
                  {taskId && !sortedTasks.some((t) => t.id === taskId) && (
                    <option value={taskId}>
                      {phaseTasksQ.isLoading ? 'Loading task…' : '(linked task)'}
                    </option>
                  )}
                  {sortedTasks.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.is_complete ? '✓ ' : ''}
                      {t.task_text}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {/* D-109 — AI drafter as a grouped, labeled "cell". Visually
                bounded so the user sees "this is its own thing", with a
                clear header + grouped controls inside. */}
            <section
              className="border rounded-md overflow-hidden"
              style={{
                background: 'color-mix(in srgb, var(--color-section-header) 18%, var(--color-card))',
                borderColor: 'var(--color-border)',
              }}
            >
              <header
                className="px-3.5 py-2 border-b flex items-center gap-2"
                style={{
                  background: 'color-mix(in srgb, var(--color-section-header) 50%, var(--color-card))',
                  borderColor: 'var(--color-border)',
                }}
              >
                <SparkleIcon />
                <h4 className="text-xs uppercase tracking-wide font-semibold text-ink">
                  Draft with AI
                </h4>
                <span className="text-[11px] text-ink-2">
                  · pulls your notes + recent published updates as source material
                </span>
              </header>
              <div className="p-3.5 space-y-3">
                {/* AI model + source scope row */}
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="AI model"
                    hint="Mock = deterministic test. Qwen = local. Claude = cloud."
                  >
                    <select
                      value={aiProvider}
                      onChange={(e) =>
                        setAiProvider(e.target.value as AIProviderName)
                      }
                      className={selectClass}
                    >
                      <option value="mock">Mock (deterministic)</option>
                      <option value="local">Qwen 27B (local)</option>
                      <option value="cloud">Claude (cloud)</option>
                    </select>
                  </Field>
                  <Field
                    label="Draft AI from"
                    hint="Entire project notes (broadest context) or a specific phase's notes."
                  >
                    <select
                      value={aiContextPhaseId}
                      onChange={(e) => setAiContextPhaseId(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">Entire project notes</option>
                      {sortedPhases.map((p) => (
                        <option key={p.id} value={p.id}>
                          {String(p.phase_number).padStart(2, '0')} — {p.phase_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                {/* Prompt + Draft button */}
                <Field
                  label="Tell the AI what to draft"
                  hint="Paste a paragraph of rough jot-notes or write an instruction. The AI uses your selected source above plus this text."
                >
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      // Ctrl/Cmd+Enter → fire the Draft action without
                      // forcing a mouse trip to the button. Useful when
                      // you've pasted a paragraph and are ready to draft.
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        if (aiPrompt.trim()) draftMut.mutate();
                      }
                    }}
                    placeholder="e.g. draft an update on foundation pour status and cooler delivery — or paste raw jot-notes and ask the AI to clean them up"
                    rows={4}
                    className={`${inputClass} leading-snug resize-y`}
                    style={{ minHeight: '5em' }}
                  />
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-[10px] num text-ink-3">
                      Ctrl+Enter to draft
                    </span>
                    <button
                      type="button"
                      onClick={() => draftMut.mutate()}
                      disabled={draftMut.isPending || !aiPrompt.trim()}
                      className="px-4 py-1.5 rounded text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                      style={{ background: 'var(--color-accent)' }}
                    >
                      {draftMut.isPending ? 'Drafting…' : 'Draft'}
                    </button>
                  </div>
                </Field>
                {aiError && (
                  <p
                    className="text-xs num"
                    style={{ color: 'var(--color-danger)' }}
                  >
                    {aiError}
                    {aiProvider !== 'mock' && (
                      <>
                        {' — '}
                        <button
                          type="button"
                          className="underline"
                          onClick={() => {
                            setAiProvider(
                              aiProvider === 'local' ? 'cloud' : 'local'
                            );
                            setAiError(null);
                          }}
                        >
                          Try {aiProvider === 'local' ? 'Claude' : 'Qwen'} instead?
                        </button>
                      </>
                    )}
                  </p>
                )}
              </div>
            </section>

            {/* Public body (was "Published summary") */}
            <Field
              label="Public update — visible to client"
              hint="This is what the client reads when you publish. Plain language."
            >
              <textarea
                value={publishedSummary}
                onChange={(e) => setPublishedSummary(e.target.value)}
                rows={6}
                className={`${inputClass} leading-relaxed`}
                placeholder="What you want the client to read…"
              />
            </Field>

            {/* Private body (was "Private entry") */}
            <Field
              label="Private notes — for you only"
              hint="Internal context. Never leaves your machine. Use for parts you don't want the client to see."
            >
              <textarea
                value={privateEntry}
                onChange={(e) => setPrivateEntry(e.target.value)}
                rows={3}
                className={`${inputClass} leading-relaxed`}
                placeholder="Notes for yourself / future C…"
              />
            </Field>

            {/* Publish toggle */}
            <label className="flex items-center gap-2 text-sm text-ink cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="w-4 h-4 accent-orange-500"
              />
              <span>Publish to client portal</span>
              {isPublished ? (
                <span className="text-[11px] text-ink-3 ml-1">
                  · on: goes live on the portal
                </span>
              ) : (
                <span className="text-[11px] text-ink-3 ml-1">
                  · off: stays as a draft
                </span>
              )}
              {isPublished && (
                <span
                  aria-hidden
                  className="inline-block w-2 h-2 rounded-full ml-1"
                  style={{ background: 'var(--color-success)' }}
                />
              )}
            </label>
            {saveMut.isError && (
              <p
                className="text-xs num"
                style={{ color: 'var(--color-danger)' }}
              >
                {(saveMut.error as Error).message}
              </p>
            )}
          </div>

          <footer
            className="px-5 py-3 border-t flex items-center justify-between gap-3 rounded-b-lg"
            style={{
              background: 'var(--color-section-header)',
              borderColor: 'var(--color-border)',
            }}
          >
            <div>
              {mode.kind === 'edit' && (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteOpen(true)}
                  className="text-xs underline"
                  style={{ color: 'var(--color-danger)' }}
                >
                  Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded text-sm border border-border bg-white hover:bg-[var(--color-section-header)] focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={() => saveMut.mutate(isPublished)}
                disabled={saveMut.isPending}
                className="px-3 py-1.5 rounded text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                style={{
                  background: isPublished
                    ? 'var(--color-accent)'
                    : 'var(--color-navy)',
                }}
              >
                {saveMut.isPending
                  ? 'Saving…'
                  : isPublished
                    ? 'Save & publish'
                    : 'Save draft'}
              </button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>

      {/* D-110 — styled delete dialog (D-050 pattern, replaces native confirm). */}
      {mode.kind === 'edit' && (
        <ConfirmationDialog
          open={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
          variant="danger"
          title="Delete this update?"
          description="The update will be removed from this project — Recent Updates, the Client Updates tab, and any linked task it references. This cannot be undone."
          confirmLabel="Yes, delete"
          pending={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate()}
        />
      )}
    </Dialog.Root>
  );
}

const inputClass =
  'w-full border border-border rounded px-2 py-1.5 text-sm text-ink bg-white focus:outline-none focus:border-border-strong focus:ring-2 focus:ring-accent';

const selectClass = inputClass;

function Field({
  label,
  children,
  className = '',
  hint,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
  hint?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-xs text-ink-2 font-medium">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-ink-3 italic">{hint}</span>}
    </label>
  );
}

function UrgentToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (b: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs font-medium focus:outline-none focus:ring-2 focus:ring-accent transition-colors self-end"
      style={
        value
          ? {
              background: 'var(--color-danger-soft)',
              borderColor: 'var(--color-danger)',
              color: 'var(--color-danger)',
            }
          : {
              background: '#FFFFFF',
              borderColor: 'var(--color-border)',
              color: 'var(--color-ink-2)',
            }
      }
      title={
        value
          ? 'Marked urgent — visible flag on the row. Click to unmark.'
          : 'Mark this update as urgent (red flag on the row, like task is_critical).'
      }
      aria-pressed={value}
    >
      <FlagIcon filled={value} />
      <span>{value ? 'Urgent' : 'Mark urgent'}</span>
    </button>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * D-109 — Find the project's notes section that best matches the given
 * phase, so the AI drafter can read the right bucket without making the
 * user pick one explicitly. Tries (1) exact display-name match, (2)
 * section_key matches the phase name normalized to snake_case, (3) the
 * phase's first word matches the section_key. Returns undefined if no
 * match — the server then falls back to project-wide context.
 */
function deriveSectionKeyFromPhase(
  phaseId: string,
  phases: Array<{ id: string; phase_name: string }>,
  sections: Array<{ section_key: string; section_display_name: string }>,
): string | undefined {
  const phase = phases.find((p) => p.id === phaseId);
  if (!phase) return undefined;
  const phaseName = phase.phase_name.toLowerCase();
  const phaseSnake = phaseName.replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
  const phaseFirstWord = phaseName.split(/\s+/)[0];

  const byExactDisplay = sections.find(
    (s) => s.section_display_name.toLowerCase() === phaseName,
  );
  if (byExactDisplay) return byExactDisplay.section_key;

  const bySnake = sections.find((s) => s.section_key === phaseSnake);
  if (bySnake) return bySnake.section_key;

  const byFirstWord = sections.find((s) => s.section_key === phaseFirstWord);
  if (byFirstWord) return byFirstWord.section_key;

  return undefined;
}

function SparkleIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill="currentColor"
      style={{ color: 'var(--color-accent)' }}
      aria-hidden
    >
      <path d="M8 1.5l1.4 3.6 3.6 1.4-3.6 1.4L8 11.5 6.6 7.9 3 6.5l3.6-1.4L8 1.5z" />
      <path d="M13 11l.7 1.5 1.5.5-1.5.5L13 15l-.7-1.5-1.5-.5 1.5-.5L13 11z" opacity="0.6" />
    </svg>
  );
}
